/**
 * Collection spotlight markup builder — the storefront's copy of the designer's
 * `spotlightMarkup.js`, so the published section and the canvas preview cannot
 * drift.
 *
 * ONE collection, shown as its first few products under the collection's name.
 * Several collections shown as cards is Featured collections
 * (`collectionsMarkup.js`).
 *
 * The rail shell and the product cards are Featured products' own, so a
 * spotlight's cards look exactly like the products section's and share its CSS
 * (`.sf-products-sec`).
 */

import { escapeHtml } from '../../utilities/helper';
import { copyOf, accentTextOf } from './bannerMarkup';
import { productCardHtml, ARROW_PREV, ARROW_NEXT } from './productsMarkup';

/** How many of the collection's products a spotlight shows. */
export const SPOTLIGHT_PRODUCT_LIMIT = 8;

/**
 * @param {{ subtitle?: string, visible?: boolean }} block
 * @param {{ id: *, name: string, description?: string } | null} collection
 * @param {Array} products  records already through `normalizeProduct`
 * @param {{ headingFont?: string, themeColor?: string }} options
 * @returns {string} markup, or '' when there is nothing to show
 */
export const buildSpotlightHtml = (block = {}, collection = null, products = [], options = {}) => {
  const { headingFont, themeColor } = options;

  const list = Array.isArray(products) ? products.filter(Boolean).slice(0, SPOTLIGHT_PRODUCT_LIMIT) : [];
  if (!collection || !list.length || block.visible === false) return '';

  /* The heading IS the collection's name — `block.title` is ignored, because
     some migrated blocks still carry a stale auto-stamped title. The subheading
     is only what the partner wrote; the collection's description is written for
     its card and page, and is deliberately not a fallback. */
  const heading = String(collection.name || '').trim() || 'Featured collection';
  const sub = copyOf(block.subtitle);
  const headFont = headingFont ? `font-family:${headingFont};` : '';
  const href = `/collections/${encodeURIComponent(collection.id)}`;

  return `<section class="sf-sec sf-products-sec sf-spotlight-sec">
  <div class="sf-in">
    <div class="sf-sh">
      <div>
        <h2 style="${headFont}">${escapeHtml(heading)}</h2>
        ${sub ? `<div class="sub">${escapeHtml(sub)}</div>` : ''}
      </div>
      <a class="sf-seeall" href="${escapeHtml(href)}" style="color:${escapeHtml(themeColor ? accentTextOf(themeColor) : '#2C2F32')}">View collection &rarr;</a>
    </div>
    <div class="sf-railwrap">
      <div class="sf-arrows">
        <button class="sf-arr prev scroll-left" type="button" aria-label="Scroll back">${ARROW_PREV}</button>
        <button class="sf-arr next scroll-right" type="button" aria-label="Scroll forward">${ARROW_NEXT}</button>
      </div>
      <div class="sf-rail cards-scroller">${list.map(productCardHtml).join('')}</div>
    </div>
  </div>
</section>`;
};

export default buildSpotlightHtml;
